import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { useEffect } from 'react';
import { keyBy } from 'lodash';
import submitLead from '../../Helpers/submitLead';
import { SUBMIT_LEAD_GQL } from '../../Helpers/submission';
import { getPreSubmissionComponents } from '../../Components/__selectors__/component.selectors';
import {
  shouldMatchWithSuppliersViaLegalStatement,
  shouldShowMultiSupplierMatchStatement,
} from '../../Helpers/conditionality';
import { getLegalStatementToShow } from '../../Components/LegalStatement/legalStatement.helpers';
import { updateSelectAndConnectBrandSubmissionStatus } from '../../Lib/redux/slices/submission';
import SubmissionStatus from '../../Data/SubmissionStatus';

const useResubmissions = () => {
  const { useCidFromURL, iFrameId, widgetLabel, testMode } = useSelector(
    (store) => store.optionalFormConfig
  );
  const oneTrustLocale = useSelector((store) => store.oneTrustLocale);
  const { currentPage } = useSelector((store) => store.navigation);
  const { selectAndConnectBrands, submissionBlocks } = useSelector(
    (store) => store.submission
  );
  const isSubmissionBlocked = submissionBlocks.length > 0;
  const {
    countryCode,
    phoneNumberCountryCode,
    formId,
    campaignId,
    sourceSite,
    pageUrl,
    referrerUrl,
    isDomainInternal,
    localeCode,
    consentCertification,
    supplierId,
    privacyPolicy,
    navigationBarButtonTextConfig,
  } = useSelector((store) => store.formConfig);
  const {
    consentedSuppliersHash,
    consentedLegalStatementHash,
    supplierLegalStatements,
    statement,
  } = useSelector((store) => store.legalStatement);
  const components = useSelector((store) => store.components);
  const { ancestorSid, id, shouldResubmit, firstSubmissionAttemptTimestamp } =
    useSelector((store) => store.submission);
  const { parentPageCookies } = useSelector((store) => store.cookies);
  const { eventPublicKeyString } = useSelector((store) => store.publicKeys);
  const { submissionPageNumber, subcategoryName } = useSelector(
    (store) => store.formConfig
  );
  const {
    customerMatches: customerBrandMatches,
    subcategoryMatches: subcategoryBrandMatches,
    matchLevel,
    supplierMatchSelectionEnabled,
  } = useSelector((store) => store.supplierMatches);
  const { visitorId } = useSelector((store) => store.userIdentities);
  const {
    singleSupplierConsentedLegalStatementHash,
    singleSupplierStatement,
    multipleSupplierStatement: multipleSupplierMatchStatement,
    multipleSupplierConsentedLegalStatementHash,
  } = supplierLegalStatements;
  const shouldShowMatchInLegalStatement =
    shouldMatchWithSuppliersViaLegalStatement({
      components,
      singleSupplierStatement,
    });
  const showMultiSupplierMatchStatement = shouldShowMultiSupplierMatchStatement(
    {
      components,
      multipleSupplierMatchStatement,
    }
  );
  const [submitLeadMutation] = useMutation(SUBMIT_LEAD_GQL);
  const dispatch = useDispatch();
  const preSubmissionComponents = getPreSubmissionComponents(
    components,
    submissionPageNumber
  );

  const privacyPolicyUrl = privacyPolicy?.link || '';

  const consentStatement = getLegalStatementToShow({
    components,
    singleSupplierStatement,
    statement,
    shouldShowMatchInLegalStatement,
    multipleSupplierMatchStatement,
    showMultiSupplierMatchStatement,
    currentPage,
    submitButtonText: navigationBarButtonTextConfig?.submit ?? '',
  });

  /**
   * If the state indicates that we should resubmit we should try to do so and if the submission is successful then submitLead will set shouldResubmit to false
   */
  useEffect(() => {
    if (shouldResubmit === true && !isSubmissionBlocked) {
      if (id !== undefined) {
        /**
         * Note the appointmentBooking is not included here as there is no resubmission for appointments
         */
        submitLead({
          ancestorSid,
          campaignId,
          components: preSubmissionComponents,
          consentedLegalStatementHash,
          consentedSuppliersHash,
          countryCode,
          localeCode,
          oneTrustLocale,
          pageUrl,
          parentPageCookies,
          phoneNumberCountryCode,
          referrerUrl,
          sourceSite,
          testMode,
          useCidFromURL,
          widgetLabel,
          dispatch,
          currentPage,
          iFrameId,
          submitLeadMutation,
          eventPublicKeyString,
          formId,
          isDomainInternal,
          submissionPageNumber,
          isResubmission: true,
          customerBrandMatches,
          subcategoryBrandMatches,
          shouldShowMatchInLegalStatement,
          singleSupplierConsentedLegalStatementHash,
          showMultiSupplierMatchStatement,
          multipleSupplierConsentedLegalStatementHash,
          consentCertification,
          supplierId,
          visitorId,
          privacyPolicyUrl,
          consentStatement,
          subcategoryName,
          firstSubmissionAttemptTimestamp,
          matchLevel,
          supplierMatchSelectionEnabled,
        });
      }
    }
  }, [shouldResubmit]);

  useEffect(() => {
    if (
      !isSubmissionBlocked &&
      selectAndConnectBrands &&
      Object.keys(selectAndConnectBrands).length > 0
    ) {
      // if any select and connect brands are not started, set them to in progress and try to submit them
      Object.values(selectAndConnectBrands).forEach((brand) => {
        if (brand.submissionStatus === SubmissionStatus.NOT_STARTED) {
          dispatch(
            updateSelectAndConnectBrandSubmissionStatus({
              brandId: brand.brandId,
              submissionStatus: SubmissionStatus.IN_PROGRESS,
            })
          );

          const selectAndConnectComponents = Object.values(
            preSubmissionComponents
          ).map((component) => {
            if (
              component.component.type === 'hidden' &&
              component.component.text === 'Brand (hidden)'
            ) {
              return {
                ...component,
                component: {
                  ...component.component,
                  defaultValue: brand.brandId,
                },
              };
            }
            return component;
          });

          submitLead({
            ancestorSid,
            campaignId,
            components: keyBy(
              selectAndConnectComponents,
              (component) => `component_${component.component.id}`
            ),
            consentedLegalStatementHash,
            consentedSuppliersHash,
            countryCode,
            localeCode,
            oneTrustLocale,
            pageUrl,
            parentPageCookies,
            phoneNumberCountryCode,
            referrerUrl,
            sourceSite,
            testMode,
            useCidFromURL,
            widgetLabel,
            dispatch,
            currentPage,
            iFrameId,
            submitLeadMutation,
            eventPublicKeyString,
            formId,
            isDomainInternal,
            submissionPageNumber,
            isResubmission: true,
            customerBrandMatches,
            subcategoryBrandMatches,
            shouldShowMatchInLegalStatement: false,
            singleSupplierConsentedLegalStatementHash,
            showMultiSupplierMatchStatement: true,
            multipleSupplierConsentedLegalStatementHash,
            consentCertification,
            supplierId,
            visitorId,
            privacyPolicyUrl,
            consentStatement,
            subcategoryName,
            firstSubmissionAttemptTimestamp,
            matchLevel,
            supplierMatchSelectionEnabled,
            selectAndConnectBrand: {
              brandId: brand.brandId,
              brandRevisionId: brand.brandRevisionId,
            },
          });
        }
      });
    }
  }, [selectAndConnectBrands]);

  return null;
};

export default useResubmissions;
